import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import { getToken } from "@/shared/lib/token";
import {
    UserGetDTO,
    MeUpdateDTO,
    ProfileImageUpdateDTO,
    FileGetDTO
} from "@/shared/types/shared/me";
import {
    WorkerTaskRequestDTO,
    PageWorkerTasksGetDTO,
    WorkerTaskGetDTO,
} from "@/shared/types/project";
import type { FaceEnrollmentResponseDTO, FaceStatusDTO } from "@/shared/types/shared/face";

let profileImagePromise: Promise<string> | null = null;

/** Um ou N arquivos vão no mesmo campo "files"; a API aceita repetição. */
function appendTaskEvidences(formData: FormData, files?: File | File[]): void {
    if (!files) return;
    const list = Array.isArray(files) ? files : [files];
    list.forEach((file) => formData.append("files", file));
}

export const meService = {
    getMe: async (): Promise<UserGetDTO> => {
        const { data } = await api.get<UserGetDTO>("/me", {
            skipErrorToast: true
        });
        return data;
    },

    /** Papéis e permissões da sessão atual na organização do token. */
    getAccess: async (): Promise<{ roles: string[]; permissions: string[] }> => {
        const { data } = await api.get<{ roles: string[]; permissions: string[] }>("/me/access", { skipErrorToast: true });
        return data;
    },

    updateMe: async (payload: MeUpdateDTO): Promise<UserGetDTO> => {
        const { data } = await api.put<UserGetDTO>("/me", payload);
        return data;
    },

    getMyTasks: async (
        params: WorkerTaskRequestDTO
    ): Promise<PageWorkerTasksGetDTO> => {
        const response = await api.get<PageWorkerTasksGetDTO>("/me/tasks", {
            params: {
                page: params.page - 1,
                size: params.size,
                sort: params.sort,
                ...params.filter,
            },
        });
        return response.data;
    },

    getProfileImage: async (): Promise<string> => {
        if (profileImagePromise) return profileImagePromise;

        profileImagePromise = (async () => {
            try {
                const response = await api.get(`/me/profile/img`, {
                    responseType: "blob",
                    skipErrorToast: true
                });

                const blob = response.data;
                return new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (error) {
                profileImagePromise = null; // Permite tentar novamente se falhar
                return "";
            }
        })();

        return profileImagePromise;
    },

    updateProfileImage: async (file: File): Promise<FileGetDTO> => {
        const formData = new FormData();
        formData.append("file", file);

        const { data } = await api.put<FileGetDTO>("/me/profile/img", formData);
        return data;
    },

    getProfileImageByUsername: async (username: string): Promise<string> => {
        try {
            const response = await api.get(`/users/${username}/profile/img`, {
                responseType: "blob",
                skipErrorToast: true
            });

            const blob = response.data;
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            return "";
        }
    },

    getFaceStatus: async (): Promise<FaceStatusDTO> => {
        const { data } = await api.get<FaceStatusDTO>("/me/face");
        return data;
    },

    /** Foto cadastrada como object URL (o endpoint exige o JWT no header). Quem chama revoga. */
    getFaceImageUrl: async (): Promise<string> => {
        const response = await api.get("/me/face/image", {
            responseType: "blob",
            skipErrorToast: true,
        });
        return URL.createObjectURL(response.data as Blob);
    },

    enrollFace: async (image: File): Promise<FaceEnrollmentResponseDTO> => {
        const formData = new FormData();
        formData.append("image", image);
        const { data } = await api.put<FaceEnrollmentResponseDTO>("/me/face", formData);
        return data;
    },

    startTask: async (
        taskId: string,
        files?: File | File[],
        observation?: string
    ): Promise<void> => {
        const formData = new FormData();
        appendTaskEvidences(formData, files);
        if (observation) formData.append("observation", observation);

        await api.put(`/me/tasks/${taskId}/start`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    finishTask: async (
        taskId: string,
        files?: File | File[],
        observation?: string
    ): Promise<void> => {
        const formData = new FormData();
        appendTaskEvidences(formData, files);
        if (observation) formData.append("observation", observation);

        await api.put(`/me/tasks/${taskId}/finish`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    
    getTaskById: async (taskId: string): Promise<WorkerTaskGetDTO> => {
        const { data } = await api.get<WorkerTaskGetDTO>(`/me/tasks/${taskId}`);
        return data;
    },
};